"use client"

import { useRef, useState, useEffect, useCallback } from "react"
import { cn } from "@/lib/utils"
import { Presentation, ChevronLeft, ChevronRight, FileDown } from "lucide-react"

export interface PptSlide {
  title: string
  bulletPoints?: string[]
  /** Gradient for the slide background preview */
  gradient?: string
}

export function PptPreviewCard({
  filename,
  pageCount,
  slides,
  className,
}: {
  filename: string
  pageCount: number
  slides: PptSlide[]
  className?: string
}) {
  const [currentSlide, setCurrentSlide] = useState(0)
  const scrollRef = useRef<HTMLDivElement>(null)

  // Sync dot indicator with scroll position via IntersectionObserver
  const slideRefs = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    const container = scrollRef.current
    if (!container) return
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
            const idx = slideRefs.current.indexOf(entry.target as HTMLDivElement)
            if (idx >= 0) setCurrentSlide(idx)
          }
        }
      },
      { root: container, threshold: 0.6 }
    )
    slideRefs.current.forEach((el) => { if (el) observer.observe(el) })
    return () => observer.disconnect()
  }, [slides.length])

  const scrollToSlide = useCallback((idx: number) => {
    const el = slideRefs.current[idx]
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" })
    }
  }, [])

  const goPrev = () => {
    const next = Math.max(0, currentSlide - 1)
    setCurrentSlide(next)
    scrollToSlide(next)
  }

  const goNext = () => {
    const next = Math.min(slides.length - 1, currentSlide + 1)
    setCurrentSlide(next)
    scrollToSlide(next)
  }

  return (
    <div className={cn(
      "rounded-xl border border-border/60 bg-card/60 backdrop-blur-sm overflow-hidden",
      className,
    )}>
      {/* Header */}
      <div className="px-4 py-2.5 border-b border-border/40 bg-secondary/20 flex items-center gap-2">
        <Presentation className="h-4 w-4 text-orange-500" />
        <span className="text-xs font-semibold text-foreground flex-1 truncate">{filename}</span>
        <span className="text-micro px-1.5 py-0.5 rounded-full bg-orange-500/10 text-orange-600 dark:text-orange-400 font-medium">
          {pageCount} 页
        </span>
        <button className="flex h-6 w-6 items-center justify-center rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors">
          <FileDown className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Carousel — horizontal scroll-snap with peek */}
      <div className="p-3 pb-1">
        <div
          ref={scrollRef}
          className="flex gap-2.5 overflow-x-auto scroll-smooth no-scrollbar"
          style={{
            scrollSnapType: "x mandatory",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
          }}
        >
          {slides.map((slide, i) => (
            <div
              key={i}
              ref={(el) => { slideRefs.current[i] = el }}
              className="shrink-0 rounded-lg overflow-hidden aspect-[16/9]"
              style={{
                width: slides.length > 1 ? "82%" : "100%",
                scrollSnapAlign: "start",
                background: slide?.gradient || "linear-gradient(135deg, #1e293b 0%, #334155 100%)",
              }}
            >
              {/* Slide content */}
              <div className="relative w-full h-full p-4 flex flex-col justify-between">
                {/* Slide title */}
                {slide?.title && (
                  <div>
                    <p className="text-xs font-semibold text-white drop-shadow-md truncate">
                      {slide.title}
                    </p>
                    <div className="w-1/2 h-1 rounded bg-white/20 mt-1.5" />
                  </div>
                )}

                {/* Bullet points */}
                <div className="space-y-1.5 mt-auto">
                  {(slide?.bulletPoints || ["", "", ""]).map((_, bi) => (
                    <div key={bi} className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-white/30 shrink-0" />
                      <div
                        className="h-1.5 rounded bg-white/20"
                        style={{ width: `${50 + (bi * 12) % 35}%` }}
                      />
                    </div>
                  ))}
                </div>

                {/* Page number */}
                <div className="flex justify-between items-end mt-2">
                  <div className="w-14 h-1 rounded bg-white/10" />
                  <span className="text-nano text-white/40 font-mono">
                    {i + 1}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between px-3 pb-2.5">
        <button
          onClick={goPrev}
          disabled={currentSlide === 0}
          className="flex h-6 w-6 items-center justify-center rounded-md hover:bg-secondary text-muted-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>

        {/* Slide dots */}
        <div className="flex gap-1">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => { setCurrentSlide(i); scrollToSlide(i) }}
              className={cn(
                "h-1.5 rounded-full transition-all duration-200",
                i === currentSlide
                  ? "w-4 bg-primary"
                  : "w-1.5 bg-border hover:bg-muted-foreground/40"
              )}
            />
          ))}
        </div>

        <button
          onClick={goNext}
          disabled={currentSlide === slides.length - 1}
          className="flex h-6 w-6 items-center justify-center rounded-md hover:bg-secondary text-muted-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}

